import * as React from "react";
import { Slot } from "@radix-ui/react-slot@1.1.2";
import { cva, type VariantProps } from "class-variance-authority@0.7.1";

import { cn } from "@utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-sm)] border text-sm font-medium shadow-sm transition-[background-color,border-color,color,box-shadow,filter,opacity,transform] duration-150 ease-out disabled:pointer-events-none disabled:opacity-60 aria-disabled:pointer-events-none aria-disabled:opacity-60 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-offset-background focus-visible:ring-offset-2 focus-visible:ring-[3px] active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bbb-gold-fill border-transparent text-primary-foreground hover:brightness-[1.04] hover:saturate-[1.03]",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:text-destructive-foreground active:bg-destructive/85 focus-visible:ring-destructive/25 dark:focus-visible:ring-destructive/40",
        outline:
          "border-[var(--border-strong)] bg-background text-foreground shadow-none hover:-translate-y-px hover:border-[var(--accent-line)] hover:bg-[var(--surface-highlight)] hover:text-foreground hover:shadow-[0_10px_22px_-16px_var(--glow)] active:translate-y-0 active:border-[var(--border-strong)] active:bg-[var(--surface-active)] active:shadow-none motion-reduce:hover:translate-y-0",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground shadow-none hover:-translate-y-px hover:border-[var(--accent-line)] hover:bg-[var(--surface-highlight)] hover:text-foreground hover:shadow-[0_10px_22px_-16px_var(--glow)] active:translate-y-0 active:bg-[var(--surface-active)] active:shadow-none motion-reduce:hover:translate-y-0",
        ghost:
          "button-ghost border-transparent text-foreground shadow-none hover:bg-[var(--surface-hover)] hover:text-foreground active:bg-[var(--surface-active)]",
        link: "h-auto border-transparent p-0 text-primary underline-offset-4 shadow-none hover:text-primary hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
        "icon-touch": "size-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };
